import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AlertTriangle, LucideAngularModule } from 'lucide-angular';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { ButtonTypes } from '@shared/components/input/button/button-types';
import { CdkTrapFocus } from '@angular/cdk/a11y';

/**
 * Generic confirmation dialog. Used by ConfirmDialogService — not intended
 * to be instantiated directly in templates.
 *
 * Renders a small centred modal on top of any existing UI (including open
 * modals) with a warning icon, message, and two buttons. The dialog is
 * fully keyboard-driven: Escape cancels, and focus is trapped inside.
 */
@Component({
  selector: 'app-confirm-dialog',
  imports: [TranslatePipe, LucideAngularModule, ButtonDirective, CdkTrapFocus],
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.css',
})
export class ConfirmDialogComponent implements OnInit {
  /** Translation key for the dialog title. */
  @Input() public title: string = '';

  /** Translation key for the dialog message body. */
  @Input() public message: string = '';

  /** Translation key for the confirm (destructive) action button. */
  @Input() public confirmLabel: string = 'common.confirm';

  /** Translation key for the cancel (safe) action button. */
  @Input() public cancelLabel: string = 'common.cancel';

  /** Visual treatment for the confirm button (default: danger, since this is normally destructive). */
  @Input() public confirmVariant: ButtonTypes = 'danger';

  /** Emits true when the user confirms, false when they cancel. */
  @Output() public result = new EventEmitter<boolean>();

  protected readonly AlertTriangle = AlertTriangle;

  /** Drives the fade-in animation; flipped from false → true after first render. */
  public visible = false;

  public ngOnInit(): void {
    // Defer one tick so the CSS transition has an initial state to animate from
    setTimeout(() => (this.visible = true));
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onEscape(event: Event): void {
    if (!(event instanceof KeyboardEvent)) return;
    event.stopPropagation();
    event.preventDefault();
    this.cancel();
  }

  public confirm(): void {
    this.result.emit(true);
  }

  public cancel(): void {
    this.result.emit(false);
  }
}
